import React, { useEffect, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import Link from "../../components/Link";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  Card,
  CardContent,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { AdminContainer } from "../../components/layouts";
import api from "../../api";
import dayjs from "dayjs";

const STATUS_COLORS = {
  success: { bg: "#e8f5e9", color: "#2e7d32" },
  failed: { bg: "#ffebee", color: "#c62828" },
  running: { bg: "#fff3e0", color: "#e65100" },
  waiting: { bg: "#e3f2fd", color: "#1565c0" },
  paused: { bg: "#f3e5f5", color: "#6a1b9a" },
  cancelled: { bg: "#efebe9", color: "#4e342e" },
  skipped: { bg: "#f5f5f5", color: "#757575" }
};

const TRIGGER_TYPE_LABELS = {
  "trigger.lead.created": "New lead added",
  "trigger.lead.updated": "Lead stage changed",
  "trigger.application.updated": "Application status change",
  "trigger.schedule": "Scheduled workflow"
};

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  infoGrid: {
    marginBottom: theme.spacing(3)
  },
  infoLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginBottom: 2
  },
  infoValue: {
    fontWeight: 500
  },
  section: {
    marginTop: theme.spacing(3)
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600
  },
  nodeRunRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  jsonBlock: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: 400,
    overflow: "auto",
    padding: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    fontSize: "0.8rem",
    fontFamily: "monospace"
  },
  dataSection: {
    padding: theme.spacing(2),
    backgroundColor: "#fafafa"
  },
  dataGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2)
  },
  noWrap: {
    whiteSpace: "nowrap"
  }
}));

function formatDuration(startedAt, finishedAt) {
  if (!startedAt || !finishedAt) return "—";
  const ms = dayjs(finishedAt).diff(dayjs(startedAt));
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

function formatJson(data) {
  if (!data) return "null";
  try {
    if (typeof data === "string") return JSON.stringify(JSON.parse(data), null, 2);
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

function NodeRunRow({ run, classes }) {
  const [expanded, setExpanded] = useState(false);
  const statusStyle = STATUS_COLORS[run.status] || {};

  return (
    <React.Fragment>
      <TableRow
        className={classes.nodeRunRow}
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{run.nodeId || run.node_id}</TableCell>
        <TableCell>{run.nodeType || run.node_type}</TableCell>
        <TableCell>
          <Chip
            label={run.status}
            size="small"
            style={{
              backgroundColor: statusStyle.bg,
              color: statusStyle.color,
              fontWeight: 600
            }}
          />
        </TableCell>
        <TableCell className={classes.noWrap}>
          {run.startedAt || run.started_at
            ? dayjs(run.startedAt || run.started_at).format("HH:mm:ss.SSS")
            : "—"}
        </TableCell>
        <TableCell className={classes.noWrap}>
          {formatDuration(
            run.startedAt || run.started_at,
            run.finishedAt || run.finished_at
          )}
        </TableCell>
        <TableCell className={classes.noWrap}>
          {(run.attemptNumber || run.attempt_number || 1)} / {(run.maxAttempts || run.max_attempts || 1)}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={7} style={{ padding: 0 }}>
            <Box className={classes.dataSection}>
              <Box className={classes.dataGrid}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Input</Typography>
                  <pre className={classes.jsonBlock}>
                    {formatJson(run.input)}
                  </pre>
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Output</Typography>
                  <pre className={classes.jsonBlock}>
                    {formatJson(run.output)}
                  </pre>
                </Box>
              </Box>
              {(run.errorMessage || run.error_message) && (
                <Box mt={2}>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Error
                  </Typography>
                  <pre className={classes.jsonBlock} style={{ backgroundColor: "#fff5f5" }}>
                    {run.errorMessage || run.error_message}
                  </pre>
                </Box>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}

export default function WorkflowExecutionDetailPage() {
  const classes = useStyles();
  const history = useHistory();
  const { executionId } = useParams();
  const location = useLocation();
  const workflowId = new URLSearchParams(location.search).get("workflow_id");

  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (workflowId) params.workflow_id = workflowId;

    api
      .getWorkflowExecutionDetail(executionId, params)
      .then(api.pickData)
      .then((data) => {
        setExecution(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch execution detail:", err);
        setLoading(false);
      });
  }, [executionId, workflowId]);

  if (loading) {
    return (
      <AdminContainer>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </AdminContainer>
    );
  }

  if (!execution) {
    return (
      <AdminContainer>
        <Typography color="error">Execution not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.goBack()}>
          Back
        </Button>
      </AdminContainer>
    );
  }

  const triggerType = execution.triggerNodeKind || execution.trigger_node_kind;
  const statusStyle = STATUS_COLORS[execution.status] || {};
  const runs = execution.runs || [];
  const waits = execution.waits || [];

  return (
    <AdminContainer>
      {/* Header */}
      <Box className={classes.header}>
        <IconButton size="small" onClick={() => history.push("/workflow-execution-log")}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" color="primary">
          Execution #{execution.executionNumber || execution.execution_number}
        </Typography>
        <Chip
          label={execution.status}
          style={{
            backgroundColor: statusStyle.bg,
            color: statusStyle.color,
            fontWeight: 600
          }}
        />
      </Box>

      {/* Info Cards */}
      <Grid container spacing={2} className={classes.infoGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography className={classes.infoLabel}>Workflow</Typography>
              <Typography className={classes.infoValue}>
                {execution.workflowName || execution.workflow_name || "Untitled"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography className={classes.infoLabel}>Partner</Typography>
              <Typography className={classes.infoValue}>
                <Link to={`/partners/${execution.partnerId || execution.partner_id}`}>
                  {execution.partnerName || execution.partner_name || execution.partnerId || execution.partner_id}
                </Link>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography className={classes.infoLabel}>Trigger Type</Typography>
              <Typography className={classes.infoValue}>
                {TRIGGER_TYPE_LABELS[triggerType] || triggerType || "—"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography className={classes.infoLabel}>Duration</Typography>
              <Typography className={classes.infoValue}>
                {formatDuration(
                  execution.startedAt || execution.started_at,
                  execution.finishedAt || execution.finished_at
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography className={classes.infoLabel}>Started At</Typography>
              <Typography className={classes.infoValue}>
                {execution.startedAt || execution.started_at
                  ? dayjs(execution.startedAt || execution.started_at).format("MMM D, YYYY HH:mm:ss")
                  : "—"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography className={classes.infoLabel}>Finished At</Typography>
              <Typography className={classes.infoValue}>
                {execution.finishedAt || execution.finished_at
                  ? dayjs(execution.finishedAt || execution.finished_at).format("MMM D, YYYY HH:mm:ss")
                  : "—"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography className={classes.infoLabel}>Subject</Typography>
              <Typography className={classes.infoValue}>
                {execution.subjectType || execution.subject_type
                  ? `${execution.subjectType || execution.subject_type} #${execution.subjectId || execution.subject_id}`
                  : "—"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography className={classes.infoLabel}>Snapshot Version</Typography>
              <Typography className={classes.infoValue}>
                {execution.snapshotVersion || execution.snapshot_version || "—"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Message */}
      {(execution.errorMessage || execution.error_message) && (
        <Box mb={3}>
          <Paper style={{ padding: 16, backgroundColor: "#fff5f5", border: "1px solid #ffcdd2" }}>
            <Typography variant="subtitle2" color="error" gutterBottom>
              Error Message
            </Typography>
            <Typography variant="body2" style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
              {execution.errorMessage || execution.error_message}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Node Runs */}
      <Box className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle} color="primary">
          Node Runs ({runs.length})
        </Typography>
        {runs.length === 0 ? (
          <Typography color="textSecondary">No node runs recorded</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 48 }} />
                  <TableCell>Node ID</TableCell>
                  <TableCell>Node Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Started At</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Attempt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((run) => (
                  <NodeRunRow
                    key={run.id}
                    run={run}
                    classes={classes}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Waits */}
      {waits.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle} color="primary">
            Waits ({waits.length})
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Node ID</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Conditions</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {waits.map((wait) => (
                  <TableRow key={wait.id}>
                    <TableCell>{wait.nodeId || wait.node_id}</TableCell>
                    <TableCell>
                      {wait.subjectType || wait.subject_type
                        ? `${wait.subjectType || wait.subject_type} #${wait.subjectId || wait.subject_id}`
                        : "—"}
                    </TableCell>
                    <TableCell style={{ maxWidth: 300 }}>
                      <pre className={classes.jsonBlock} style={{ maxHeight: 100 }}>
                        {formatJson(wait.conditions)}
                      </pre>
                    </TableCell>
                    <TableCell className={classes.noWrap}>
                      {wait.createdAt || wait.created_at
                        ? dayjs(wait.createdAt || wait.created_at).format("MMM D, YYYY HH:mm:ss")
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Context */}
      {execution.context && (
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle} color="primary">
            Execution Context
          </Typography>
          <pre className={classes.jsonBlock}>
            {formatJson(execution.context)}
          </pre>
        </Box>
      )}
    </AdminContainer>
  );
}
